
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { getDomainById, updateDomain } from '../services/storageService';
import { Domain, AccessibilitySettings } from '../types';
import { 
  Loader2, Save, Accessibility, MousePointer2, Settings, 
  X, Globe, Link as LinkIcon, Code, Share2, ShieldCheck,
  ChevronLeft, ChevronRight, RotateCcw, Type, List, Layout as LayoutIcon, AlignLeft,
  Maximize2, Contrast, Palette, RefreshCw, Eye, Move,
  CheckCircle2, Moon, Sun, Copy, Check, User, Pause, ImageOff
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { EmbedModal } from '../components/EmbedModal';

const DEFAULT_SETTINGS: AccessibilitySettings = {
  enabled: false,
  buttonPosition: 'bottom-left',
  primaryColor: '#4f46e5',
  buttonIcon: 'universal',
};

// Helper component for different icons
const WidgetIcon: React.FC<{ type: 'universal' | 'human' | 'wheelchair', size?: number, color?: string }> = ({ type, size = 20, color = "currentColor" }) => {
  if (type === 'human') return <User size={size} color={color} />;
  if (type === 'wheelchair') return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="4" r="2"/>
      <path d="M10 13v-3a2 2 0 0 1 2-2h3"/>
      <path d="M10 13h5l2.5 4"/>
      <path d="M15 13v4"/>
      <path d="M10 19a5 5 0 1 0 5-5"/>
    </svg>
  );
  // Default to universal
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="5" r="3"/>
      <path d="M10 8h4"/><path d="M9 11h6"/><path d="M12 11v8"/><path d="M8 21h8"/>
      <path d="M10 11 8 16"/><path d="m14 11 2 5"/>
    </svg>
  );
};

const AccessibilitySettingsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [domain, setDomain] = useState<Domain | undefined>(undefined);
  const [settings, setSettings] = useState<AccessibilitySettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const { t } = useLanguage();

  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [showLinkBuilder, setShowLinkBuilder] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false); 
  const [lbSource, setLbSource] = useState('');
  const [lbCopied, setLbCopied] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const domainData = await getDomainById(id);
        if (domainData) {
          setDomain(domainData);
          setSettings(domainData.accessibilitySettings || DEFAULT_SETTINGS);
        } else {
          setError('Domain not found.');
        }
      } catch (err) {
        setError('Failed to load domain data.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleToggle = async (enabled: boolean) => {
    setSettings(prev => ({ ...prev, enabled }));
  };

  const handleSave = async () => {
    if (!id) return;
    setIsSaving(true);
    try {
      await updateDomain(id, { 
        accessibilityEnabled: settings.enabled,
        accessibilitySettings: settings 
      });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (err) {
      alert('Failed to save settings.');
    } finally {
      setIsSaving(false);
    }
  };

  const sidebarContent = id ? (
    <nav className="space-y-1">
      <Link to={`/report/${id}`} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><Share2 size={20} />{t.analytics}</Link>
      <Link to={`/report/${id}/gdpr-settings`} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><ShieldCheck size={20} />{t.gdprSettings}</Link>
      <Link to={`/report/${id}/accessibility`} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-semibold transition-colors"><Accessibility size={20} />{t.accessibility}</Link>
      <button onClick={() => setShowShareModal(true)} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><Globe size={20} />{t.shareReport}</button>
      <button onClick={() => setShowLinkBuilder(true)} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><LinkIcon size={20} />{t.linkBuilder}</button>
      <button onClick={() => setShowEmbedModal(true)} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"><Code size={20} />{t.installCode}</button>
    </nav>
  ) : null;

  if (isLoading) return <Layout sidebarContent={sidebarContent}><div className="flex h-full items-center justify-center"><Loader2 className="animate-spin text-indigo-500" size={32} /></div></Layout>;

  return (
    <Layout sidebarContent={sidebarContent}>
      <div className="max-w-7xl mx-auto">
        <div className="mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-indigo-600 text-white flex items-center justify-center rounded-2xl shadow-lg shadow-indigo-200 dark:shadow-none">
              <Accessibility size={30} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white">{t.accessibilitySettings}</h1>
              <p className="text-slate-500 dark:text-slate-400">{t.accessibilityDesc}</p>
            </div>
          </div>
          
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-8 py-3 bg-indigo-600 text-white font-bold rounded-xl flex items-center justify-center gap-2 hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/20 disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="animate-spin" size={20} /> : (showSuccess ? <CheckCircle2 size={20} /> : <Save size={20} />)}
            {showSuccess ? 'Saved' : 'Save Changes'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white dark:bg-slate-900 p-8 rounded-[32px] border border-slate-100 dark:border-slate-800 shadow-sm space-y-8">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-900 dark:text-white">{t.accessibilityEnabled}</h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Add the WCAG widget to your site</p>
                </div>
                <button
                  onClick={() => handleToggle(!settings.enabled)}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none ${settings.enabled ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-slate-700'}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${settings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>

              <div className="space-y-4">
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300 block">{t.accessibilityButtonPos}</label>
                <div className="grid grid-cols-2 gap-3">
                  {(['bottom-left', 'bottom-right', 'middle-left', 'middle-right'] as const).map(pos => (
                    <button
                      key={pos}
                      onClick={() => setSettings(s => ({ ...s, buttonPosition: pos }))}
                      className={`px-4 py-3 rounded-xl border-2 text-xs font-bold capitalize transition-all ${settings.buttonPosition === pos ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600' : 'border-slate-100 dark:border-slate-800 hover:border-slate-200 text-slate-500'}`}
                    >
                      {pos.replace('-', ' ')}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300 block">{t.accessibilityIcon}</label>
                <div className="grid grid-cols-3 gap-3">
                  {(['universal', 'human', 'wheelchair'] as const).map(icon => (
                    <button
                      key={icon}
                      onClick={() => setSettings(s => ({ ...s, buttonIcon: icon }))}
                      className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${settings.buttonIcon === icon ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600' : 'border-slate-100 dark:border-slate-800 hover:border-slate-200 text-slate-400'}`}
                    >
                      <WidgetIcon type={icon} size={24} />
                      <span className="text-[10px] font-bold uppercase">{icon}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                <label className="text-sm font-bold text-slate-700 dark:text-slate-300 block">{t.accessibilityColor}</label>
                <div className="flex items-center gap-4">
                  <input
                    type="color"
                    value={settings.primaryColor}
                    onChange={e => setSettings(s => ({ ...s, primaryColor: e.target.value }))}
                    className="w-12 h-12 rounded-lg cursor-pointer border-none bg-transparent"
                  />
                  <input
                    type="text"
                    value={settings.primaryColor}
                    onChange={e => setSettings(s => ({ ...s, primaryColor: e.target.value }))}
                    className="flex-1 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-mono"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="bg-slate-50 dark:bg-slate-950 rounded-[32px] border-2 border-dashed border-slate-200 dark:border-slate-800 p-8 min-h-[600px] relative overflow-hidden flex flex-col items-center justify-center">
               <div className="absolute top-6 left-8 flex items-center gap-2 text-xs font-bold text-slate-400 uppercase tracking-widest">
                 <Eye size={14} /> {t.accessibilityPreview}
               </div>

               <div className={`w-full max-w-[360px] bg-white dark:bg-slate-900 rounded-[32px] shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden animate-in fade-in zoom-in-95 duration-500 transition-opacity flex flex-col max-h-[550px] ${!settings.enabled ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}>
                  <div className="bg-slate-900 text-white p-5 flex justify-between items-center shrink-0">
                    <div>
                      <h4 className="font-bold text-sm font-sans">Accessibility Adjustments</h4>
                      <p className="text-[10px] text-slate-500 font-medium font-sans">Project: {domain?.name}</p>
                    </div>
                    <X size={20} className="text-slate-500" />
                  </div>

                  <div className="flex-1 overflow-y-auto p-5 space-y-6 scrollbar-thin">
                    <button className="w-full py-2.5 bg-slate-100 dark:bg-slate-800 rounded-xl text-[12px] font-bold text-slate-600 dark:text-slate-300 flex items-center justify-center gap-2 hover:bg-slate-200 transition-colors font-sans">
                      <RotateCcw size={14} strokeWidth={3} /> Reset settings
                    </button>

                    {/* Basic Grid */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">
                           <span className="flex items-center gap-1.5"><Type size={12} /> Font Size</span>
                           <span>100%</span>
                        </div>
                        <div className="flex items-center bg-slate-50 dark:bg-slate-800/50 rounded-xl p-1 border border-slate-100 dark:border-slate-800">
                           <button className="flex-1 h-8 flex items-center justify-center hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-all shadow-sm"><ChevronLeft size={16}/></button>
                           <button className="flex-1 h-8 flex items-center justify-center hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-all shadow-sm"><ChevronRight size={16}/></button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-[10px] font-bold text-slate-500 uppercase tracking-wider font-sans">
                           <span className="flex items-center gap-1.5"><AlignLeft size={12} /> Line Height</span>
                           <span>100%</span>
                        </div>
                        <div className="flex items-center bg-slate-50 dark:bg-slate-800/50 rounded-xl p-1 border border-slate-100 dark:border-slate-800">
                           <button className="flex-1 h-8 flex items-center justify-center hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-all shadow-sm"><ChevronLeft size={16}/></button>
                           <button className="flex-1 h-8 flex items-center justify-center hover:bg-white dark:hover:bg-slate-700 rounded-lg transition-all shadow-sm"><ChevronRight size={16}/></button>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h5 className="text-[9px] font-extrabold text-slate-400 uppercase tracking-widest border-b border-slate-50 dark:border-slate-800 pb-2 font-sans">Visual Adjustments</h5>
                      <div className="grid grid-cols-2 gap-2.5">
                        {[
                          { icon: <Moon size={18} />, label: 'Dark Mode' },
                          { icon: <Sun size={18} />, label: 'Light Mode' },
                          { icon: <RefreshCw size={18} />, label: 'Invert' },
                          { icon: <Contrast size={18} />, label: 'High Contrast' },
                          { icon: <Palette size={18} />, label: 'Monochrome' },
                          { icon: <Maximize2 size={18} />, label: 'Big Cursor' }
                        ].map((btn, i) => (
                          <button key={i} className={`flex flex-col items-center gap-1.5 p-3.5 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-2xl hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all ${i === 0 ? 'bg-indigo-50 border-indigo-200' : ''}`}>
                             <div className={`${i === 0 ? 'text-indigo-600' : 'text-slate-400'}`}>{btn.icon}</div>
                             <span className={`text-[9px] font-bold text-center font-sans leading-tight ${i === 0 ? 'text-indigo-700' : 'text-slate-600'}`}>{btn.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* New Focus Assist Section */}
                    <div className="space-y-3">
                      <h5 className="text-[9px] font-extrabold text-slate-400 uppercase tracking-widest border-b border-slate-50 dark:border-slate-800 pb-2 font-sans">Focus Assist</h5>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { label: 'Highlight Titles', val: 'U' },
                          { label: 'Highlight Links', icon: <LinkIcon size={16} /> },
                          { label: 'Readable Font', icon: <Type size={16} /> }
                        ].map((btn, i) => (
                          <button key={i} className="flex flex-col items-center gap-2 p-3 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-2xl hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-all">
                             <div className="text-slate-500 font-bold h-5 flex items-center">{btn.val || btn.icon}</div>
                             <span className="text-[8px] font-bold text-center text-slate-600 dark:text-slate-400 leading-tight">{btn.label}</span>
                          </button>
                        ))}
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-2">
                         <button className="flex flex-col items-center gap-2 p-3 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-2xl hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-all">
                            <Maximize2 size={16} className="text-slate-500" />
                            <span className="text-[8px] font-bold text-center text-slate-600 dark:text-slate-400 leading-tight">Content Scaling</span>
                         </button>
                         <button className="flex flex-col items-center gap-2 p-3 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-2xl hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-all">
                            <MousePointer2 size={16} className="text-slate-500" />
                            <span className="text-[8px] font-bold text-center text-slate-600 dark:text-slate-400 leading-tight">Focus Assist</span>
                         </button>
                      </div>
                    </div>

                    {/* New Content & Motion Section */}
                    <div className="space-y-3 pb-4">
                      <h5 className="text-[9px] font-extrabold text-slate-400 uppercase tracking-widest border-b border-slate-50 dark:border-slate-800 pb-2 font-sans">Content & Motion</h5>
                      <div className="grid grid-cols-2 gap-2.5">
                         <button className="flex flex-col items-center gap-2 p-4 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-[20px] hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-all">
                            <Pause size={18} className="text-slate-500" />
                            <span className="text-[9px] font-bold text-center text-slate-600 dark:text-slate-400">Pause Animations</span>
                         </button>
                         <button className="flex flex-col items-center gap-2 p-4 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-800 rounded-[20px] hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-all">
                            <ImageOff size={18} className="text-slate-500" />
                            <span className="text-[9px] font-bold text-center text-slate-600 dark:text-slate-400">Hide Images</span>
                         </button>
                      </div>
                    </div>

                  </div>
               </div>

               <div className={`absolute pointer-events-none transition-all duration-500 ${!settings.enabled ? 'opacity-0 scale-50' : 'opacity-100 scale-100'} ${
                 settings.buttonPosition === 'bottom-left' ? 'bottom-12 left-12' :
                 settings.buttonPosition === 'bottom-right' ? 'bottom-12 right-12' :
                 settings.buttonPosition === 'middle-left' ? 'top-1/2 left-8 -translate-y-1/2' :
                 'top-1/2 right-8 -translate-y-1/2'
               }`}>
                  <div className="w-14 h-14 rounded-full flex items-center justify-center text-white shadow-2xl animate-bounce" style={{ backgroundColor: settings.primaryColor }}>
                     <WidgetIcon type={settings.buttonIcon} size={28} color="white" />
                  </div>
               </div>
            </div>
          </div>
        </div>
      </div>
      
      {showLinkBuilder && domain && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[32px] p-8 max-w-md w-full shadow-2xl border border-slate-100 dark:border-slate-800">
             <div className="flex justify-between items-start mb-6">
               <h3 className="text-xl font-bold text-slate-900 dark:text-white">{t.linkBuilderTitle}</h3>
               <button onClick={() => setShowLinkBuilder(false)}><X size={24} className="text-slate-400" /></button>
             </div>
             <div className="space-y-4">
               <div>
                 <label className="text-xs font-bold uppercase text-slate-500 dark:text-slate-400 tracking-wider font-sans">{t.sourceName}</label>
                 <input 
                   className="w-full mt-2 p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl outline-none focus:border-indigo-500 text-slate-900 dark:text-white"
                   placeholder="e.g. facebook"
                   value={lbSource}
                   onChange={(e) => setLbSource(e.target.value)}
                 />
                 <div className="flex flex-wrap gap-2 mt-3">
                    {['Facebook', 'Google', 'Instagram', 'Yelp', 'TikTok'].map(s => (
                       <button
                          key={s}
                          onClick={() => setLbSource(s.toLowerCase())}
                          className="px-3 py-1.5 text-xs font-medium bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 rounded-lg hover:bg-indigo-50 hover:text-indigo-600 transition-colors font-sans"
                       >
                          {s}
                       </button>
                    ))}
                 </div>
               </div>
               <div className="p-4 bg-slate-900 dark:bg-black rounded-xl text-indigo-200 font-mono text-xs break-all relative">
                  {domain.url}/?s={lbSource.trim() || 'source'}
                  <button 
                    onClick={() => { navigator.clipboard.writeText(`${domain.url}/?s=${lbSource.trim()}`); setLbCopied(true); setTimeout(() => setLbCopied(false), 2000); }}
                    className="absolute top-2 right-2 p-1.5 bg-white/10 rounded hover:bg-white/20 text-white"
                  >
                    {lbCopied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
               </div>
             </div>
          </div>
        </div>
      )}

      {showShareModal && domain && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-[32px] p-8 max-w-md w-full shadow-2xl border border-slate-100 dark:border-slate-800">
             <div className="flex justify-between items-start mb-6">
               <h3 className="text-xl font-bold text-slate-900 dark:text-white font-sans">{t.shareReportTitle}</h3>
               <button onClick={() => setShowShareModal(false)}><X size={24} className="text-slate-400" /></button>
             </div>
             <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-xl text-indigo-700 dark:text-indigo-300 font-mono text-xs break-all relative">
                  {window.location.origin}/#/public/{domain.id}
                  <button 
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/#/public/${domain.id}`); setShareCopied(true); setTimeout(() => setShareCopied(false), 2000); }}
                    className="absolute top-2 right-2 p-1.5 bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200 rounded-lg"
                  >
                    {shareCopied ? <Check size={14} /> : <Copy size={14} />}
                  </button>
             </div>
          </div>
        </div>
      )}

      {showEmbedModal && domain && <EmbedModal domain={domain} onClose={() => setShowEmbedModal(false)} />}
    </Layout>
  );
};

export default AccessibilitySettingsPage;
